import { useMemo } from 'react'
import ErrorBoundary from '../ErrorBoundary'

import {
	ArrowBack as ArrowBackIcon,
	Download as DownloadIcon,
	Insights as InsightsIcon,
	Lock as LockIcon,
	MoreVert as MoreVertIcon,
	Person as PersonIcon,
} from '@mui/icons-material'

import {
	CircularProgress,
	Menu,
	Tooltip,
} from '@mui/material'

import {
	formatDateLabel,
	formatGroupParticipantsUpdateText,
	formatPhoneNumber,
	formatTimestampLabel,
	isGroupParticipantsUpdateMessage,
} from '../../services/scout'
import { renderExportConversation } from '../../services/utils'

import ConversationMessageBody, {
	ForwardedMessageLabel,
	getMessageIdentifier,
	getMessageKey,
	getMessageReactions,
	isCardMessage,
	isMessageForwarded,
	shouldShowDateSeparator,
} from './ConversationMessageBody'
import ConversationAvatar from './ConversationAvatar'
import EditableField from '../ui/EditableField'

const normalizeParticipantDirectoryKey = (value = '') => {
	if (typeof value === 'string' || typeof value === 'number') {
		return String(value).trim()
	}

	if (!value || typeof value !== 'object') {
		return ''
	}

	const candidate = value?.participant
		|| value?.participant_original
		|| value?.participant_jid
		|| value?.participant_lid
		|| value?.jid
		|| value?.id
		|| value?.lid
		|| value?.phoneNumber
		|| value?.phone_number
		|| value?.participant_phone

	if (typeof candidate === 'string' || typeof candidate === 'number') {
		return String(candidate).trim()
	}

	return ''
}

const buildParticipantDirectoryVariants = (value) => {
	const normalizedValue = normalizeParticipantDirectoryKey(value)
	if (!normalizedValue) {
		return []
	}

	const rawLower = normalizedValue.toLowerCase()
	const digits = normalizedValue.split('@')[0].replace(/\D/g, '')
	const variants = new Set([normalizedValue, rawLower])

	if (digits) {
		variants.add(digits)
	}

	return Array.from(variants)
}

const normalizeDisplayNameScalar = (value = '') => {
	if (typeof value === 'string' || typeof value === 'number') {
		return String(value).trim()
	}

	return ''
}

const getParticipantDisplayName = (message) => {
	const pushName = String(message?.pushName || message?.push_name || '').trim()
	if (!pushName || pushName.includes('@') || /^\d{8,}$/.test(pushName)) {
		return 'Participante'
	}

	return pushName
}

const ConversationThread = ({
	selectedConversation,
	selectedAccount,
	getStableConversationTitle,
	optionsAnchorEl,
	optionsOpen,
	onOpenOptions,
	onCloseOptions,
	onBack,
	onExportConversation,
	onShowInsights,
	onShowProfile,
	onSaveConversationTitle,
	isEditableContactConversation,
	enterprise,
	initialMessagesLoading,
	threadRef,
	onThreadScroll,
	renderedMessages,
	messagesLoading,
	loadingMediaIds,
	onLoadMediaMessage,
	messages,
	searchValue,
	onOpenEditHistory,
	messageReactionLookup,
}) => {
	const participantDirectory = useMemo(() => {
		const nextDirectory = new Map()

		if (!Array.isArray(messages)) {
			return nextDirectory
		}

		messages.forEach((entry) => {
			if (!isGroupParticipantsUpdateMessage(entry)) {
				const participantName = normalizeDisplayNameScalar(entry?.pushName || entry?.push_name || entry?.sender)
				if (participantName) {
					const identifiers = [
						entry?.participant,
						entry?.key?.participant,
					]

					identifiers.forEach((identifier) => {
						buildParticipantDirectoryVariants(identifier).forEach((variant) => {
							if (!nextDirectory.has(variant)) {
								nextDirectory.set(variant, participantName)
							}
						})
					})
				}
			}

		})

		return nextDirectory
	}, [messages])

	return (
		<section className="scout-panel scout-panel--thread">
			<div className="scout-thread-header">
				<button
					type="button"
					className="scout-back-link scout-back-link--mobile-only"
					onClick={onBack}
				>
					<ArrowBackIcon />
					<span>Voltar</span>
				</button>
				<div className="scout-thread-header__identity">
					<ConversationAvatar conversation={selectedConversation} displayName={getStableConversationTitle(selectedConversation)} className="scout-conversation-row__avatar scout-thread-header__avatar" />
					<div>
						{isEditableContactConversation(selectedConversation) ? (
							<EditableField
								className="scout-thread-header__title"
								value={getStableConversationTitle(selectedConversation)}
								placeholder="Clique para adicionar um nome"
								onSave={onSaveConversationTitle}
							/>
						) : (
							<strong>{getStableConversationTitle(selectedConversation)}</strong>
						)}
						<span>{selectedConversation?.phone_num ? formatPhoneNumber(selectedConversation.phone_num) : 'Sem número identificado'}</span>
					</div>
				</div>
				<div className="scout-thread-header__actions">
					<Tooltip title="Mais opções">
						<button type="button" className="scout-icon-button" onClick={onOpenOptions}>
							<MoreVertIcon />
						</button>
					</Tooltip>
					<Menu
						anchorEl={optionsAnchorEl}
						open={optionsOpen}
						onClose={onCloseOptions}
						transformOrigin={{ horizontal: 'right', vertical: 'top' }}
						anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
						PaperProps={{ className: 'scout-support-menu' }}
					>
						<div className="scout-support-menu__items">
							<button type="button" className="scout-support-menu__item" onClick={onExportConversation}>
								{renderExportConversation(enterprise) ? <DownloadIcon /> : <LockIcon />}
								<span>Exportar mensagens</span>
							</button>
							<button type="button" className="scout-support-menu__item" onClick={onShowInsights}>
								<InsightsIcon />
								<span>Insights com IA</span>
							</button>
							<button type="button" className="scout-support-menu__item" onClick={onShowProfile}>
								<PersonIcon />
								<span>Perfil do contato</span>
							</button>
						</div>
					</Menu>
				</div>
			</div>

			{initialMessagesLoading
				? <div className="scout-thread-messages scout-thread-messages--loading">
					<CircularProgress />
				</div>
				: <div className="scout-thread-messages" ref={threadRef} onScroll={onThreadScroll}>
					{renderedMessages.length
						? renderedMessages.map((message, index) => {
							const fromMe = Boolean(message.fromMe)
							const isRestricted = message?.restricted === true
							const isGroupParticipantsUpdate = isGroupParticipantsUpdateMessage(message)
							const previousMessage = index > 0 ? renderedMessages[index - 1] : null
							const reactions = getMessageReactions(message, messageReactionLookup)

							return (
								<div key={getMessageKey(message, index)} className={shouldShowDateSeparator(message, previousMessage) ? 'scout-message-group scout-message-group--with-date' : 'scout-message-group'}>
									{shouldShowDateSeparator(message, previousMessage) ? (
										<div className="scout-message-date-separator">{formatDateLabel(Number(message.timestamp) * 1000)}</div>
									) : null}
									<article className={`scout-message ${isGroupParticipantsUpdate ? 'scout-message--system' : ''} ${fromMe ? 'scout-message--from-me' : ''} ${isRestricted ? 'restricted' : ''} ${reactions.length ? 'scout-message--has-reactions' : ''}`.trim()}>
										{isGroupParticipantsUpdate ? (
												<div className="scout-message__system">{formatGroupParticipantsUpdateText(message, {
													connectedPhone: selectedAccount?.phone_number,
													connectedName: selectedAccount?.name,
													participantDirectory,
												})}</div>
										) : (
											<div className={`scout-message__bubble scout-conversation-box ${isCardMessage(message) ? 'scout-message__bubble--card' : ''}`.trim()} data-index={index}>
											{isMessageForwarded(message) ? <ForwardedMessageLabel /> : null}
											<strong>{fromMe ? selectedAccount?.name || 'Atendente' : selectedConversation?.type === 'GROUP' ? getParticipantDisplayName(message) : getStableConversationTitle(selectedConversation) || 'Contato'}</strong>												<ConversationMessageBody
													message={message}
													onLoadMedia={onLoadMediaMessage}
													messages={messages}
													mediaLoading={Boolean(loadingMediaIds[getMessageIdentifier(message, index)])}
													searchValue={searchValue}
													enterprise={enterprise}
													onOpenEditHistory={onOpenEditHistory}
												/>
												<small>{message.iso_time || formatTimestampLabel(message.timestamp)}</small>
												{reactions.length ? (
													<div className={`scout-message__reactions ${fromMe ? 'scout-message__reactions--from-me' : ''}`.trim()} aria-label="Reações da mensagem">
														{reactions.map((reaction) => (
															<span key={reaction.id} className="scout-message__reaction-chip">{reaction.emoji}</span>
														))}
													</div>
												) : null}
											</div>
										)}
									</article>
								</div>
							)
						})
						: <div className="scout-panel__empty scout-panel__empty--thread">Nenhuma mensagem encontrada para esta conversa.</div>
					}
					{messagesLoading && renderedMessages.length
						? <div className="scout-thread-loading scout-thread-loading--inline">
							Carregando mensagens antigas...
						</div>
						: null
					}
				</div>
			}
		</section>
	)
}

const ConversationThreadWithBoundary = (props) => (
    <ErrorBoundary
        componentName="ConversationThread"
        showReload
        logToBackend
        title="Erro no thread de mensagens"
        message="Ocorreu um erro ao exibir as mensagens desta conversa. Tente novamente."
    >
        <ConversationThread {...props} />
    </ErrorBoundary>
)

export default ConversationThreadWithBoundary
